// Otello come app sul telefono (app web installabile, docs/mobile-web/README
// step 8). Qui solo cio' che serve a proporre l'installazione:
// - Android/Chrome manda una volta `beforeinstallprompt`: lo si tiene da
//   parte e si usa quando l'utente tocca "Installa l'app";
// - su iPhone non esiste una richiesta automatica: si spiega come fare.
// Gia' aperta come app (schermata Home) non si propone nulla.

let deferredPrompt = null;
let started = false;
const listeners = new Set();

const notify = () => listeners.forEach((listener) => listener());

const getDefaultWindow = () => (typeof window !== 'undefined' ? window : null);
const getDefaultNavigator = () =>
  typeof navigator !== 'undefined' ? navigator : null;

const startPwaInstallListener = ({
  windowObject = getDefaultWindow(),
} = {}) => {
  if (started || typeof windowObject?.addEventListener !== 'function') {
    return false;
  }
  started = true;
  windowObject.addEventListener('beforeinstallprompt', (event) => {
    // Niente barra automatica di Chrome: si propone dal menu account.
    event.preventDefault?.();
    deferredPrompt = event;
    notify();
  });
  windowObject.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
  return true;
};

const isStandaloneDisplay = ({
  windowObject = getDefaultWindow(),
  navigatorObject = getDefaultNavigator(),
} = {}) => {
  if (navigatorObject?.standalone === true) return true;
  try {
    return Boolean(
      windowObject?.matchMedia?.('(display-mode: standalone)')?.matches
    );
  } catch (error) {
    return false;
  }
};

// iPhone e iPad (iPadOS si presenta come Mac, ma col tocco).
const isIosDevice = (navigatorObject = getDefaultNavigator()) => {
  const userAgent = String(navigatorObject?.userAgent || '');
  if (/iPhone|iPad|iPod/i.test(userAgent)) return true;
  return (
    navigatorObject?.platform === 'MacIntel' &&
    Number(navigatorObject?.maxTouchPoints) > 1
  );
};

// La guida spiega i passi di Safari: altri browser su iPhone (Chrome,
// Firefox, Edge, app Google) li hanno altrove, e dentro le app (Gmail,
// Instagram, Facebook...) non si puo' aggiungere alla Home.
const IOS_NOT_SAFARI =
  /CriOS|FxiOS|EdgiOS|OPiOS|GSA\/|FBAN|FBAV|Instagram|Line\/|DuckDuckGo|YaBrowser/i;

const isIosSafari = (navigatorObject = getDefaultNavigator()) => {
  if (!isIosDevice(navigatorObject)) return false;
  const userAgent = String(navigatorObject?.userAgent || '');
  return /Safari\//.test(userAgent) && !IOS_NOT_SAFARI.test(userAgent);
};

// Telefono o tablet (dito, non mouse).
const isTouchScreen = (windowObject = getDefaultWindow()) => {
  try {
    return Boolean(windowObject?.matchMedia?.('(pointer: coarse)')?.matches);
  } catch (error) {
    return false;
  }
};

// Il manifest (che rende Otello installabile) si collega solo su Otello da
// mobile da telefono: sul computer Chrome non propone di installarlo. Il
// layout lo indica in <meta name="otello-pwa-manifest">.
const enablePwaManifest = ({
  documentObject = typeof document !== 'undefined' ? document : null,
} = {}) => {
  const head = documentObject?.head;
  if (!head || head.querySelector('link[rel="manifest"]')) return false;
  const href = head
    .querySelector('meta[name="otello-pwa-manifest"]')
    ?.getAttribute('content');
  if (!href) return false;
  const link = documentObject.createElement('link');
  link.rel = 'manifest';
  link.href = href;
  head.appendChild(link);
  return true;
};

// Colore della barra di stato (app installata, barra di Safari/Chrome): su
// Otello da mobile come lo sfondo dell'header (bianco, scuro col tema
// scuro) al posto del blu del layout. Spegnendo torna quello di prima.
const THEME_COLOR_ORIGINAL_ATTR = 'data-otello-original-content';
const MOBILE_THEME_COLORS = { light: '#ffffff', dark: '#111318' };

const applyMobileThemeColor = ({
  enabled,
  documentObject = typeof document !== 'undefined' ? document : null,
} = {}) => {
  const meta = documentObject?.head?.querySelector?.(
    'meta[name="theme-color"]'
  );
  if (!meta) return false;
  if (enabled) {
    if (!meta.hasAttribute(THEME_COLOR_ORIGINAL_ATTR)) {
      meta.setAttribute(
        THEME_COLOR_ORIGINAL_ATTR,
        meta.getAttribute('content') || ''
      );
    }
    const isDark = Boolean(documentObject.body?.classList?.contains('dark'));
    meta.setAttribute(
      'content',
      isDark ? MOBILE_THEME_COLORS.dark : MOBILE_THEME_COLORS.light
    );
    return true;
  }
  if (!meta.hasAttribute(THEME_COLOR_ORIGINAL_ATTR)) return false;
  meta.setAttribute('content', meta.getAttribute(THEME_COLOR_ORIGINAL_ATTR));
  meta.removeAttribute(THEME_COLOR_ORIGINAL_ATTR);
  return true;
};

// Tema cambiato (chiaro/scuro): si aggiorna solo se Otello da mobile e' attivo.
const refreshMobileThemeColor = ({
  documentObject = typeof document !== 'undefined' ? document : null,
} = {}) => {
  const meta = documentObject?.head?.querySelector?.(
    'meta[name="theme-color"]'
  );
  if (!meta?.hasAttribute(THEME_COLOR_ORIGINAL_ATTR)) return false;
  return applyMobileThemeColor({ enabled: true, documentObject });
};

// 'prompt' (Android: richiesta del browser), 'ios' (istruzioni) o null.
const resolvePwaInstallMode = ({
  isStandalone = false,
  hasPrompt = false,
  isIos = false,
} = {}) => {
  if (isStandalone) return null;
  if (hasPrompt) return 'prompt';
  if (isIos) return 'ios';
  return null;
};

const hasPwaInstallPrompt = () => Boolean(deferredPrompt);

const promptPwaInstall = async () => {
  const prompt = deferredPrompt;
  if (!prompt) return 'unavailable';
  // La richiesta del browser si usa una volta sola.
  deferredPrompt = null;
  notify();
  try {
    await prompt.prompt();
    const choice = await prompt.userChoice;
    return choice?.outcome || 'dismissed';
  } catch (error) {
    return 'unavailable';
  }
};

const onPwaInstallChange = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Solo per i test.
const resetPwaInstallState = () => {
  deferredPrompt = null;
  started = false;
  listeners.clear();
};

export {
  applyMobileThemeColor,
  enablePwaManifest,
  hasPwaInstallPrompt,
  isIosDevice,
  isIosSafari,
  isStandaloneDisplay,
  onPwaInstallChange,
  promptPwaInstall,
  refreshMobileThemeColor,
  resetPwaInstallState,
  isTouchScreen,
  resolvePwaInstallMode,
  startPwaInstallListener,
};
